import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField, required, validate } from '@angular/forms/signals';
import {
  SignalCheckbox,
  SignalDropdown,
  SignalFormDirective,
  SignalNumberField,
  SignalTextField,
} from '@vallift/ngl';
import type { BoundValue, CurrencyConfig, FormField } from '../../../../models/built-form';
import { CURRENCY_DEFINITIONS } from '@shared/components/currency-input/currency.catalog';

/** Sentinel for the config dropdown “All” option (empty string is treated as unselected). */
const NO_DEFAULT_CURRENCY = 'none';

interface CurrencyOptionItem {
  label: string;
  value: string;
}

interface CurrencyConfigModel {
  label: string;
  helpText: string;
  /** `'none'` means all currencies allowed — respondents can change currency. */
  currency: string;
  defaultAmount: number | null;
  required: boolean;
  min: number | null;
  max: number | null;
  disabled: boolean;
}

@Component({
  selector: 'app-currency-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalTextField,
    SignalNumberField,
    SignalCheckbox,
    SignalDropdown,
    SignalFormDirective,
  ],
  templateUrl: './currency-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrencyConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly currencyOptions: CurrencyOptionItem[] = [
    { value: NO_DEFAULT_CURRENCY, label: 'Any' },
    ...CURRENCY_DEFINITIONS.map((currency) => ({
      value: currency.code,
      label: `${currency.flag} ${currency.code} — ${currency.name}`,
    })),
  ];

  readonly model = signal<CurrencyConfigModel>({
    label: '',
    helpText: '',
    currency: NO_DEFAULT_CURRENCY,
    defaultAmount: null,
    required: false,
    min: null,
    max: null,
    disabled: false,
  });

  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
    validate(schema.max, ({ value, valueOf }) => {
      const maxVal = value();
      const minVal = valueOf(schema.min);
      if (maxVal == null || minVal == null) {
        return undefined;
      }
      if (maxVal <= minVal) {
        return {
          kind: 'maxGreaterThanMin',
          message: 'Maximum amount must be greater than minimum amount',
        };
      }
      return undefined;
    });
  });

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.type !== 'currency') {
        return;
      }
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      const config = this.readCurrencyConfig(next);
      const bound = next.default;

      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        currency: this.toDropdownCurrency(config.defaultCurrency),
        defaultAmount:
          bound?.source === 'static' && typeof bound.value === 'number'
            ? bound.value
            : typeof config.defaultValue === 'number'
              ? config.defaultValue
              : null,
        required: next.required ?? validation.required ?? false,
        min: validation.min ?? null,
        max: validation.max ?? null,
        disabled: next.disabled === true,
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }

      const currency = this.fromDropdownCurrency(draft.currency);
      const nextDefault = this.toBoundDefault(draft.defaultAmount);
      const { default: _omitDefault, config: _omitConfig, ...rest } = field;
      const min = draft.min ?? undefined;
      const max =
        draft.max != null && (min == null || draft.max > min) ? draft.max : undefined;

      const nextConfig: CurrencyConfig = {
        defaultCurrency: currency,
        // All → respondents can pick any currency; a chosen default locks the picker.
        allowChangeCurrency: currency == null,
      };
      if (nextDefault?.source === 'static' && typeof nextDefault.value === 'number') {
        nextConfig.defaultValue = nextDefault.value;
      }

      this.fieldChange.emit({
        ...rest,
        label: draft.label.trim(),
        helpText: draft.helpText.trim() || undefined,
        ...(nextDefault ? { default: nextDefault } : {}),
        config: nextConfig,
        required: draft.required,
        disabled: draft.disabled,
        validation: {
          required: draft.required,
          min,
          max,
        },
        options: undefined,
        headingLevel: undefined,
        spacerSize: undefined,
      });
    });
  }

  private readCurrencyConfig(field: FormField): CurrencyConfig {
    const config = field.config;
    if (!config) {
      return {};
    }
    return {
      defaultCurrency:
        'defaultCurrency' in config && typeof config.defaultCurrency === 'string'
          ? config.defaultCurrency
          : 'defaultCurrency' in config && config.defaultCurrency === null
            ? null
            : undefined,
      defaultValue:
        'defaultValue' in config && typeof config.defaultValue === 'number'
          ? config.defaultValue
          : undefined,
      allowChangeCurrency:
        'allowChangeCurrency' in config && typeof config.allowChangeCurrency === 'boolean'
          ? config.allowChangeCurrency
          : undefined,
    };
  }

  private toBoundDefault(staticValue: number | null): BoundValue | undefined {
    if (staticValue === null || staticValue === undefined) {
      return undefined;
    }
    return { source: 'static', value: staticValue };
  }

  private toDropdownCurrency(code: string | null | undefined): string {
    const trimmed = code?.trim();
    return trimmed ? trimmed : NO_DEFAULT_CURRENCY;
  }

  private fromDropdownCurrency(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed || trimmed === NO_DEFAULT_CURRENCY) {
      return null;
    }
    return trimmed;
  }
}
